import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Box, Typography, TextField, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeMode } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { useSnack } from "../hooks/useSnack";

export default function Invitation() {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const { appTheme } = useThemeMode();
  const { setState: setAuthUser } = useAuth();
  const { showSnack, Snack } = useSnack();

  const [checking, setChecking] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null); // { invitingUserPseudo, email }
  const [invalid, setInvalid] = useState(false);

  const [pseudo, setPseudo] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`/api/proche/invite/${token}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.valid) {
          setInvalid(true);
        } else {
          setInviteInfo(data);
        }
      })
      .catch(() => setInvalid(true))
      .finally(() => setChecking(false));
  }, [token]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!pseudo || !password) return;
    setSubmitting(true);
    try {
      const res = await fetch("/api/register", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          users_pseudo: pseudo,
          users_email: inviteInfo.email,
          users_password: password,
          invitationToken: token,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        showSnack(data.message || t("invitation.registerError"), "error");
        return;
      }

      // Connexion automatique
      const loginRes = await fetch("/api/signIn", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          users_email: inviteInfo.email,
          users_password: password,
        }),
      });
      const loginData = await loginRes.json();
      if (loginRes.ok) {
        setAuthUser(loginData.login);
        navigate("/gauges");
      } else {
        showSnack(t("invitation.accountCreated"), "success");
        navigate("/connexion");
      }
    } catch {
      showSnack(t("common.networkError"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const containerStyle = {
    minHeight: "100vh",
    backgroundColor: appTheme.bg,
    color: appTheme.text,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "2rem",
  };

  if (checking) {
    return (
      <div style={containerStyle}>
        <CircularProgress sx={{ color: "#93c5fd" }} />
      </div>
    );
  }

  if (invalid) {
    return (
      <div style={containerStyle}>
        <Typography variant="h5" sx={{ color: "#f87171", mb: 2 }}>
          {t("invitation.invalid")}
        </Typography>
        <Typography sx={{ color: "#94a3b8", mb: 3 }}>
          {t("invitation.invalidDesc")}
        </Typography>
        <Button component={Link} to="/" sx={{ color: "#93c5fd" }}>
          {t("invitation.backHome")}
        </Button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {Snack}
      <Box
        sx={{
          backgroundColor: "#0f172a",
          borderRadius: 2,
          padding: "2rem",
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
        }}
      >
        <Typography variant="h5" sx={{ color: "#e2e8f0", mb: 1, fontWeight: "bold" }}>
          {t("invitation.title")}
        </Typography>
        <Typography sx={{ color: "#94a3b8", mb: 3, fontSize: "0.95rem" }}>
          <strong style={{ color: "#93c5fd" }}>{inviteInfo.invitingUserPseudo}</strong>{" "}
          {t("invitation.descriptionSuffix")}
        </Typography>

        <Box component="form" onSubmit={handleRegister} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Email"
            value={inviteInfo.email}
            disabled
            size="small"
            InputLabelProps={{ style: { color: "#64748b" } }}
            InputProps={{ style: { color: "#64748b", backgroundColor: "#1e293b" } }}
          />
          <TextField
            label={t("invitation.pseudo")}
            value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            required
            size="small"
            InputLabelProps={{ style: { color: "#94a3b8" } }}
            InputProps={{ style: { color: "#e2e8f0", backgroundColor: "#1e293b" } }}
          />
          <TextField
            label={t("invitation.password")}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            size="small"
            InputLabelProps={{ style: { color: "#94a3b8" } }}
            InputProps={{ style: { color: "#e2e8f0", backgroundColor: "#1e293b" } }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: "#1e3a5f",
              color: "#93c5fd",
              "&:hover": { backgroundColor: "#1e40af" },
              "&.Mui-disabled": { backgroundColor: "#1e293b", color: "#4b5563" },
            }}
          >
            {submitting ? t("invitation.creating") : t("invitation.createAccount")}
          </Button>
        </Box>

        <Typography sx={{ color: "#64748b", fontSize: "0.8rem", mt: 2, textAlign: "center" }}>
          {t("invitation.alreadyAccount")}{" "}
          <Link to="/connexion" style={{ color: "#93c5fd" }}>
            {t("invitation.login")}
          </Link>
        </Typography>
      </Box>
    </div>
  );
}
